import { isAntonioError } from '@ackee/antonio-core';
import { getAccessToken } from '@bear-auth/core';

import pkg from '@/../package.json' with { type: 'json' };
import config from '@/config';
import { logger } from '@/config/logger';
import { env } from '@/env';
import { getStore } from '@/services/context';
import { ErrorCode, isApiAntonioError } from '@/services/utilities/isApiError';

import { useAccessDeniedStore } from '../hooks/useAccessDeniedStore';
import { bearAuthActions } from '../services/bear-auth/config';
import { bearAuthId, demoBearAuthId } from '../services/bear-auth/create';
import { selectAuth } from '../services/slices/auth';

export async function demoUserInterceptor(request: Request) {
    const store = await getStore();
    const state = store.getState();
    const auth = selectAuth(state);
    const email = auth.data?.authInfo?.decodedIdToken?.email;

    if (email) {
        request.headers.set('x-user-email', email);
    }

    return request;
}

export async function authInterceptor(request: Request) {
    const accessToken = await getAccessToken(env.VITE_DEMO ? demoBearAuthId : bearAuthId);

    request.headers.set('Authorization', `Bearer ${accessToken}`);

    return request;
}

export async function unauthorizedInterceptor(error: Error) {
    if (isAntonioError(error) && error.response.status === 401) {
        await bearAuthActions.logout();
        logger.error(error);
        return null;
    }

    return error;
}

export async function iapRedirectInterceptor(response: Response) {
    if (!(env.VITE_USES_IAP && response.status === 401)) {
        return response;
    }

    try {
        const responseText = await response.clone().text();

        if (responseText === 'Invalid IAP credentials: empty token') {
            logger.info('Redirecting to IAP login page');

            const originalPath = window.location.pathname;

            const iapRedirectUrl = new URL(config.api.iapRedirect, config.api.base);
            iapRedirectUrl.searchParams.set('originalPath', originalPath);

            window.location.replace(iapRedirectUrl);
        }
    } catch (e) {
        logger.error(e);
    }

    return response;
}

export async function userAccessNotAllowedInterceptor(error: Error) {
    if (isApiAntonioError(error) && error.data.errorCode === ErrorCode.USER_ACCESS_NOT_ALLOWED) {
        const store = await getStore();
        const auth = selectAuth(store.getState());

        const name = auth.data?.authInfo?.name;

        logger.debug(
            'Intercepting user access not allowed error.',
            "Attempting to store user's name from auth session store in access denied store.",
            'Name from auth session store: ',
            name,
        );

        useAccessDeniedStore.getState().setAccessDenied({
            name,
        });
    }

    return error;
}

export async function versionInterceptor(request: Request) {
    request.headers.set('version', `FE_${pkg.version}`);

    return request;
}
